import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Type, Trash2, Maximize2 } from 'lucide-react';

const FONTS = [
  'Arial',
  'Helvetica',
  'Impact',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'Palatino Linotype',
  'Comic Sans MS',
  'Brush Script MT',
  'Lucida Console',
];

export default function TextLayer({ textItem, onUpdate, onDelete, containerRef }) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const t = useMemo(() => textItem || {}, [textItem]);

  const getPercentPos = useCallback((e) => {
    if (!containerRef?.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((cx - rect.left) / rect.width) * 100,
      y: ((cy - rect.top) / rect.height) * 100,
    };
  }, [containerRef]);

  const handleDragStart = useCallback((e) => {
    if (e.target.closest('[data-resize-handle]') || e.target.closest('[data-delete-handle]')) return;
    e.preventDefault();
    e.stopPropagation();
    const pos = getPercentPos(e);
    setDragStart({ mx: pos.x, my: pos.y, ox: t.x, oy: t.y });
    setDragging(true);
  }, [getPercentPos, t.x, t.y]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = getPercentPos(e);
    setDragStart({ mx: pos.x, my: pos.y, oFontSize: t.fontSize || 24 });
    setResizing(true);
  }, [getPercentPos, t.fontSize]);

  const handleMove = useCallback((e) => {
    if (!dragStart) return;
    e.preventDefault();
    const pos = getPercentPos(e);

    if (dragging) {
      const dx = pos.x - dragStart.mx;
      const dy = pos.y - dragStart.my;
      const nx = Math.max(0, Math.min(95, dragStart.ox + dx));
      const ny = Math.max(0, Math.min(95, dragStart.oy + dy));
      onUpdate({ ...t, x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 });
    } else if (resizing) {
      const delta = pos.x - dragStart.mx;
      const newSize = Math.max(8, Math.min(120, dragStart.oFontSize + delta * 0.5));
      onUpdate({ ...t, fontSize: Math.round(newSize) });
    }
  }, [dragStart, dragging, resizing, t, onUpdate, getPercentPos]);

  const handleEnd = useCallback(() => {
    setDragging(false);
    setResizing(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (dragging || resizing) {
      const onMove = (e) => handleMove(e);
      const onUp = () => handleEnd();
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
      };
    }
  }, [dragging, resizing, handleMove, handleEnd]);

  return (
    <div
      className={`absolute cursor-grab active:cursor-grabbing select-none touch-none group ${dragging ? 'opacity-90' : ''}`}
      style={{
        left: `${t.x || 10}%`,
        top: `${t.y || 10}%`,
        transform: `rotate(${t.rotation || 0}deg)`,
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      data-testid={`text-layer-${t.id}`}
    >
      <div
        className="whitespace-pre-wrap"
        style={{
          fontFamily: t.fontFamily || 'Arial',
          fontSize: `${t.fontSize || 24}px`,
          color: t.color || '#ffffff',
          letterSpacing: `${t.letterSpacing || 0}px`,
          lineHeight: t.lineHeight || 1.2,
          textShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        {t.text || 'Text'}
      </div>

      {/* Controls on hover */}
      <div className={`absolute inset-0 border-2 rounded-sm transition-opacity ${dragging || resizing ? 'border-[#00E5FF] opacity-100' : 'border-[#00E5FF]/0 group-hover:border-[#00E5FF]/40 opacity-100'}`} />

      {/* Resize handle */}
      <div
        data-resize-handle
        className="absolute -bottom-1.5 -right-1.5 w-5 h-5 flex items-center justify-center cursor-nwse-resize z-10 opacity-0 group-hover:opacity-100 transition-opacity"
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        data-testid={`text-resize-${t.id}`}
      >
        <div className="w-3 h-3 rounded-sm bg-[#00E5FF] border border-white/50 shadow-lg">
          <Maximize2 className="w-2 h-2 text-white m-auto mt-0.5" />
        </div>
      </div>

      {/* Delete handle */}
      <button
        data-delete-handle
        onClick={(e) => { e.stopPropagation(); onDelete(t.id); }}
        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        data-testid={`text-delete-${t.id}`}
      >
        <Trash2 className="w-2.5 h-2.5 text-white" />
      </button>
    </div>
  );
}

// Text control panel component
export function TextControlPanel({ textItems, activeTextId, onAdd, onUpdate, onDelete, onSelect }) {
  const activeText = textItems.find(t => t.id === activeTextId);

  return (
    <div className="space-y-3" data-testid="text-control-panel">
      {/* Add text button */}
      <button
        onClick={onAdd}
        data-testid="add-text-btn"
        className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-dashed border-slate-300 dark:border-white/10 rounded-xl hover:border-[#00E5FF]/40 transition-colors text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-[#00E5FF]"
      >
        <Type className="w-3.5 h-3.5" /> Add Text
      </button>

      {/* Text items list */}
      {textItems.map((item) => (
        <div
          key={item.id}
          onClick={() => onSelect(item.id)}
          className={`p-3 rounded-xl border cursor-pointer transition-all ${
            activeTextId === item.id
              ? 'border-[#00E5FF]/50 bg-[#00E5FF]/5'
              : 'border-slate-200 dark:border-white/10 hover:border-[#00E5FF]/30'
          }`}
          data-testid={`text-item-${item.id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate max-w-[120px]">{item.text || 'Empty text'}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
              className="text-slate-400 hover:text-rose-400 transition-colors"
              data-testid={`text-panel-delete-${item.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>

          {activeTextId === item.id && (
            <div className="space-y-2.5">
              {/* Text input */}
              <input
                type="text"
                value={item.text}
                onChange={(e) => onUpdate({ ...item, text: e.target.value })}
                placeholder="Enter text..."
                className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:border-[#00E5FF]/50"
                data-testid="text-input"
              />

              {/* Font selector */}
              <select
                value={item.fontFamily || 'Arial'}
                onChange={(e) => onUpdate({ ...item, fontFamily: e.target.value })}
                className="w-full bg-white dark:bg-[#1a1a1a] border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-xs text-slate-800 dark:text-white focus:outline-none focus:border-[#00E5FF]/50"
                data-testid="text-font-select"
              >
                {FONTS.map(f => (
                  <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-2">
                {/* Color picker */}
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={item.color || '#ffffff'}
                    onChange={(e) => onUpdate({ ...item, color: e.target.value })}
                    className="w-7 h-7 rounded border border-white/10 cursor-pointer bg-transparent"
                    data-testid="text-color-picker"
                  />
                  <span className="text-[10px] text-slate-500">Colour</span>
                </div>

                {/* Font size */}
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={item.fontSize || 24}
                    onChange={(e) => onUpdate({ ...item, fontSize: parseInt(e.target.value) || 24 })}
                    min={8}
                    max={120}
                    className="w-14 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-white text-center focus:outline-none focus:border-[#00E5FF]/50"
                    data-testid="text-size-input"
                  />
                  <span className="text-[10px] text-slate-500">Size</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Letter spacing */}
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Spacing</label>
                  <input
                    type="range"
                    min={-5}
                    max={20}
                    value={item.letterSpacing || 0}
                    onChange={(e) => onUpdate({ ...item, letterSpacing: parseFloat(e.target.value) })}
                    className="w-full accent-[#00E5FF] h-1"
                    data-testid="text-spacing-slider"
                  />
                </div>

                {/* Line height */}
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Line Height</label>
                  <input
                    type="range"
                    min={0.8}
                    max={3}
                    step={0.1}
                    value={item.lineHeight || 1.2}
                    onChange={(e) => onUpdate({ ...item, lineHeight: parseFloat(e.target.value) })}
                    className="w-full accent-[#00E5FF] h-1"
                    data-testid="text-lineheight-slider"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
